import { useMemo, Suspense, lazy } from "react"
import { usePageSEO } from "@/lib/usePageSEO"
import { useParams, Navigate } from "react-router-dom"
import { getBlogBySlug } from "./content"
import { APP_NAME } from "@/lib/appConfig"

export default function BlogPostPage() {
    const { slug } = useParams<{ slug: string }>()
    const metadata = useMemo(() => getBlogBySlug(slug ?? ""), [slug])

    usePageSEO({
        title: metadata ? `${metadata.title} | ${APP_NAME}` : `Blog | ${APP_NAME}`,
        description: metadata?.excerpt ?? `Read the latest landlord guides and property management tips on the ${APP_NAME} blog.`,
        path: metadata ? `/blog/${metadata.slug}` : "/blog",
        type: "article",
        image: metadata?.coverImage,
        jsonLd: metadata
            ? [
                  {
                      "@type": "BlogPosting",
                      headline: metadata.title,
                      description: metadata.excerpt,
                      datePublished: metadata.date,
                      dateModified: metadata.date,
                      author: { "@type": "Person", name: metadata.author },
                      publisher: {
                          "@type": "Organization",
                          name: APP_NAME,
                          logo: { "@type": "ImageObject", url: "https://rent-keep.com/app-icon.png" },
                      },
                      image: metadata.coverImage
                          ? `https://rent-keep.com${metadata.coverImage}`
                          : "https://rent-keep.com/app-icon.png",
                      keywords: metadata.tags.join(", "),
                      mainEntityOfPage: {
                          "@type": "WebPage",
                          "@id": `https://rent-keep.com/blog/${metadata.slug}`,
                      },
                  },
                  {
                      "@type": "BreadcrumbList",
                      itemListElement: [
                          { "@type": "ListItem", position: 1, name: "Home", item: "https://rent-keep.com" },
                          { "@type": "ListItem", position: 2, name: "Blog", item: "https://rent-keep.com/blog" },
                          {
                              "@type": "ListItem",
                              position: 3,
                              name: metadata.title,
                              item: `https://rent-keep.com/blog/${metadata.slug}`,
                          },
                      ],
                  },
              ]
            : undefined,
    })

    if (!metadata) {
        return <Navigate to="/blog" replace />
    }

    // Dynamically import the specific blog file to lazily load it only when active
    const DynamicContent = useMemo(() => {
        return lazy(() => import(`./content/${metadata.slug}.tsx`))
    }, [metadata.slug])

    return (
        <Suspense fallback={
            <div className="h-screen flex items-center justify-center bg-background">
                <div className="h-10 w-10 animate-spin rounded-full border-4 border-primary border-t-transparent" />
            </div>
        }>
            <DynamicContent />
        </Suspense>
    )
}
